import { atom } from 'jotai'
import type { createStore } from 'jotai'
import { hc } from 'hono/client'
import type { LabelGeneratorApi } from '@backend/api/stores/LabelGeneratorStore'
import type { LabelStorageApi } from '@backend/api/stores/LabelStorageStore'
import type { LabelSessionApi } from '@backend/api/stores/LabelSessionStore'
import { apiFetch, apiBase } from 'txom-frontend-libs'
import { trackOnboardingStep, trackChatMessage } from '../lib/posthog'
import { unstable_batchedUpdates } from 'react-dom'
import {
  detectAskedField,
  applyFieldAnswer,
  parseAIMarkers,
  getRequiredFields,
  getDoNotAskFields,
} from './fieldRegistry'
import { prefillListingAtom } from './AmazonListingsStore'

type Store = ReturnType<typeof createStore>

const api = hc<LabelGeneratorApi>(apiBase + '/api/LabelGeneratorStore', { fetch: apiFetch })
const storageApi = hc<LabelStorageApi>(apiBase + '/api/LabelStorageStore', { fetch: apiFetch })
const sessionApi = hc<LabelSessionApi>(apiBase + '/api/LabelSessionStore', { fetch: apiFetch })

// ============================================================================
// Types
// ============================================================================

export type Market = 'US' | 'CA' | 'EU' | 'UK' | 'FR'
export type ProductType = 'electronics_battery' | 'electronics_no_battery' | 'general'
export type ProductCategory = 'toy' | 'electronics' | 'general'
export type LabelMode = 'product' | 'packaging'
export type FRPackagingTemplate = 'paper' | 'paper_plastic' | 'plastic' | ''
export type LabelType = 'compliance' | 'upc'

// ============================================================================
// Label Data
// ============================================================================

export interface LabelData {
  productName: string
  modelRef: string
  manufacturerName: string
  manufacturerAddress: string
  countryOfOrigin: string
  manufacturerCountry: string
  safetyWarning: string
  material: string
  productType: ProductType
  labelMode: LabelMode
  radio: boolean
  fccId: string
  icId: string
  ecRepName: string
  ecRepAddress: string
  ecRepEmail: string
  ecRepPhone: string
  euImporterName: string
  euImporterAddress: string
  ukRepName: string
  ukRepAddress: string
  targetMarkets: Market[]
  containsBattery: boolean
  showRecycling: boolean
  upcCode: string
  frPackagingTemplate: FRPackagingTemplate
  productCategory: ProductCategory
  containsSmallParts: boolean
  ageRestrictionUnder3: boolean
  euImporterSkipped: boolean
  additionalInfo: string
}

const defaultLabelData: LabelData = {
  productName: '',
  modelRef: '',
  manufacturerName: '',
  manufacturerAddress: '',
  countryOfOrigin: '',
  manufacturerCountry: '',
  safetyWarning: '',
  material: '',
  productType: 'general',
  labelMode: 'packaging',
  radio: false,
  fccId: '',
  icId: '',
  ecRepName: '',
  ecRepAddress: '',
  ecRepEmail: '',
  ecRepPhone: '',
  euImporterName: '',
  euImporterAddress: '',
  ukRepName: '',
  ukRepAddress: '',
  targetMarkets: [],
  containsBattery: false,
  showRecycling: false,
  upcCode: '',
  frPackagingTemplate: '',
  productCategory: 'general',
  containsSmallParts: false,
  ageRestrictionUnder3: false,
  euImporterSkipped: false,
  additionalInfo: '',
}

export const labelDataAtom = atom<LabelData>(defaultLabelData)
export const labelTypeAtom = atom<LabelType | null>(null)
export type DimensionUnit = 'mm' | 'in'
export const dimensionUnitAtom = atom<DimensionUnit>('mm')

// ============================================================================
// Compliance Rule Engine
// ============================================================================

const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // Common name variants
  'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
  'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary',
  'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta',
  'Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia',
  'Spain', 'Sweden',
]

function isEUCountry(country: string): boolean {
  return EU_COUNTRIES.some((c) => c.toLowerCase() === country.toLowerCase())
}

const UK_NAMES = ['UK', 'GB', 'United Kingdom', 'Great Britain', 'England', 'Scotland', 'Wales', 'Northern Ireland']

function isUKCountry(country: string): boolean {
  return UK_NAMES.some((c) => c.toLowerCase() === country.toLowerCase())
}

// FR is an EU member state — selecting FR implies EU compliance requirements
function hasEUMarket(data: LabelData): boolean {
  return data.targetMarkets.includes('EU') || data.targetMarkets.includes('FR')
}

// CE regulated products: electronics, toys, wireless, medical
// These require CE mark + EU REP (not EU Importer)
export function isCERegulated(data: LabelData): boolean {
  return data.productCategory === 'electronics' || data.productType.startsWith('electronics') || data.productCategory === 'toy'
}

export function shouldShowCE(data: LabelData): boolean {
  return isCERegulated(data) && hasEUMarket(data)
}

export function shouldShowUKCA(data: LabelData): boolean {
  return isCERegulated(data) && data.targetMarkets.includes('UK')
}

export function shouldShowWEEE(data: LabelData): boolean {
  return data.productType.startsWith('electronics') && hasEUMarket(data)
}

// Unified crossed-bin icon: replaces separate WEEE + battery disposal icons
// Trigger: (EU || FR || UK) AND (electronics || containsBattery)
export function shouldShowCrossedBin(data: LabelData): boolean {
  const isEUorUK = hasEUMarket(data) || data.targetMarkets.includes('UK')
  return isEUorUK && (data.productType.startsWith('electronics') || data.containsBattery)
}

// EU Economic Operator rules per EU 2019/1020:
// CE products → require EU REP (Authorised Representative)
// Non-CE products → require EU Importer
export function shouldRequireECRep(data: LabelData): boolean {
  return hasEUMarket(data) && isCERegulated(data) && !isEUCountry(data.manufacturerCountry)
}

export function shouldRequireEUImporter(data: LabelData): boolean {
  return hasEUMarket(data) && !isCERegulated(data) && !isEUCountry(data.manufacturerCountry)
}

// UKCA regulated products: same scope as CE (electronics, toys, medical, PPE)
// Non-regulated products (books, general textiles, etc.) don't need UK REP
export function isUKCARegulated(data: LabelData): boolean {
  return data.productCategory === 'electronics' || data.productType.startsWith('electronics') || data.productCategory === 'toy'
}

// UK Responsible Person: required only when ALL three conditions are met:
// 1. Selling to Great Britain (England, Scotland, Wales) — UK market selected
// 2. Manufacturer is outside the UK
// 3. Product is UKCA-regulated (electronics, toys, medical, PPE)
// NOT required for: non-UK markets, UK-based manufacturers, non-regulated products,
// or products only going to Northern Ireland (follows EU rules)
export function shouldRequireUKRep(data: LabelData): boolean {
  return data.targetMarkets.includes('UK') && !isUKCountry(data.manufacturerCountry) && isUKCARegulated(data)
}

export function shouldShowFCC(data: LabelData): boolean {
  return data.productType.startsWith('electronics') && data.radio && data.targetMarkets.includes('US')
}

export function shouldShowIC(data: LabelData): boolean {
  return data.radio && data.targetMarkets.includes('CA')
}

export function shouldShowTriman(data: LabelData): boolean {
  return shouldShowFrancePackaging(data)
}

export function shouldShowFrancePackaging(data: LabelData): boolean {
  return data.targetMarkets.includes('FR') && data.labelMode === 'packaging'
}

// Italy packaging material identification (D.Lgs 116/2020)
// Required when selling to EU (which includes Italy) with packaging mode
export function shouldShowItalyPackaging(data: LabelData): boolean {
  return hasEUMarket(data) && data.labelMode === 'packaging'
}

// Whether the packaging template question should be asked during onboarding
// Needed for both France (Info-tri) and Italy (material identification)
export function needsPackagingTemplate(data: LabelData): boolean {
  return (data.targetMarkets.includes('FR') || data.targetMarkets.includes('EU')) && data.labelMode === 'packaging'
}

// ── Traceability Logic Block (MVP v1) ──
// Product Identifier = Model / SKU / Type / Batch / Serial
// Required by GPSR 2023/988 (EU), UK Product Safety, FCC/CPSC (US), CA regulated
export type TraceabilityLevel = 'mandatory' | 'recommended' | 'none'

export function getTraceabilityLevel(data: LabelData): TraceabilityLevel {
  // EU or UK → always mandatory (GPSR 2023/988 / UK Product Safety)
  if (hasEUMarket(data) || data.targetMarkets.includes('UK')) {
    return 'mandatory'
  }
  // US or CA → mandatory for FCC devices and children's products, recommended otherwise
  if (data.targetMarkets.includes('US') || data.targetMarkets.includes('CA')) {
    if (data.radio || data.productCategory === 'toy') {
      return 'mandatory'
    }
    return 'recommended'
  }
  // No regulated market selected yet
  if (data.targetMarkets.length === 0) return 'none'
  return 'recommended'
}

export function isTraceabilityRequired(data: LabelData): boolean {
  return getTraceabilityLevel(data) === 'mandatory'
}

export interface InfoTriLine {
  material: string
  instruction: string
}

export function getInfoTriLines(template: FRPackagingTemplate): InfoTriLine[] {
  switch (template) {
    case 'paper':
      return [{ material: 'Boîte en carton', instruction: 'À recycler' }]
    case 'paper_plastic':
      return [
        { material: 'Boîte en carton', instruction: 'À recycler' },
        { material: 'Sachet plastique', instruction: 'À recycler' },
      ]
    case 'plastic':
      return [{ material: 'Emballage plastique', instruction: 'À recycler' }]
    default:
      return []
  }
}

export interface ItalyPackagingLine {
  material: string
  code: string
  collection: string
}

export function getItalyPackagingLines(template: FRPackagingTemplate): ItalyPackagingLine[] {
  switch (template) {
    case 'paper':
      return [{ material: 'Cartone ondulato', code: 'PAP 20', collection: 'Raccolta carta' }]
    case 'paper_plastic':
      return [
        { material: 'Cartone ondulato', code: 'PAP 20', collection: 'Raccolta carta' },
        { material: 'Film in polietilene', code: 'LDPE 4', collection: 'Raccolta plastica' },
      ]
    case 'plastic':
      return [{ material: 'Film in polietilene', code: 'LDPE 4', collection: 'Raccolta plastica' }]
    default:
      return []
  }
}

export const FR_PACKAGING_TEMPLATE_OPTIONS = [
  { id: 'paper' as FRPackagingTemplate, label: 'Paper box only', icon: '📦' },
  { id: 'paper_plastic' as FRPackagingTemplate, label: 'Paper box + Plastic bag', icon: '📦🛍️' },
  { id: 'plastic' as FRPackagingTemplate, label: 'Plastic bag only', icon: '🛍️' },
] as const

export function shouldShowBilingualWarning(data: LabelData): boolean {
  return data.targetMarkets.includes('CA')
}

// Toy Warning Block
export function shouldShowToyWarning(data: LabelData): boolean {
  return data.productCategory === 'toy' && (data.containsSmallParts || data.ageRestrictionUnder3)
}

export interface ToyWarningText {
  lang: string
  lines: string[]
}

// Generate ordered toy warning texts based on target markets
// Order: US → UK → EU → FR → CA
export function getToyWarningTexts(data: LabelData): ToyWarningText[] {
  if (!shouldShowToyWarning(data)) return []
  const texts: ToyWarningText[] = []
  const markets = data.targetMarkets

  if (markets.includes('US')) {
    texts.push({
      lang: 'EN (US)',
      lines: [
        'WARNING: CHOKING HAZARD – Small parts.',
        'Not for children under 3 years.',
      ],
    })
  }

  if (markets.includes('UK')) {
    texts.push({
      lang: 'EN (UK)',
      lines: [
        'WARNING:',
        'Not suitable for children under 3 years.',
        'Small parts.',
      ],
    })
  }

  // EU (excluding FR which gets its own French block)
  if (markets.includes('EU')) {
    // English for general EU
    if (!markets.includes('UK')) {
      texts.push({
        lang: 'EN (EU)',
        lines: [
          'WARNING:',
          'Not suitable for children under 3 years.',
          'Small parts.',
        ],
      })
    }
    // German for EU (Germany is a major EU market)
    texts.push({
      lang: 'DE',
      lines: [
        'WARNUNG:',
        'Nicht geeignet für Kinder unter 3 Jahren.',
        'Enthält verschluckbare Kleinteile.',
      ],
    })
  }

  if (markets.includes('FR')) {
    texts.push({
      lang: 'FR',
      lines: [
        'AVERTISSEMENT :',
        'Ne convient pas aux enfants de moins de 3 ans.',
        'Présence de petites pièces.',
      ],
    })
  }

  if (markets.includes('CA')) {
    // English (if not already covered by US)
    if (!markets.includes('US')) {
      texts.push({
        lang: 'EN (CA)',
        lines: [
          'WARNING: CHOKING HAZARD – Small parts.',
          'Not for children under 3 years.',
        ],
      })
    }
    // French for Canada (if not already covered by FR)
    if (!markets.includes('FR')) {
      texts.push({
        lang: 'FR (CA)',
        lines: [
          'AVERTISSEMENT : RISQUE D\'ÉTOUFFEMENT – Petites pièces.',
          'Ne convient pas aux enfants de moins de 3 ans.',
        ],
      })
    }
  }

  return texts
}

// Derived: validation errors
export interface ValidationError { field: string; message: string }

export const validationErrorsAtom = atom<ValidationError[]>((get) => {
  const data = get(labelDataAtom)
  const errors: ValidationError[] = []

  // Basic required fields
  if (!data.productName) errors.push({ field: 'productName', message: 'Product name is required' })
  if (!data.manufacturerName) errors.push({ field: 'manufacturerName', message: 'Manufacturer name is required' })
  if (!data.manufacturerAddress) errors.push({ field: 'manufacturerAddress', message: 'Manufacturer address is required' })
  if (!data.countryOfOrigin) errors.push({ field: 'countryOfOrigin', message: 'Country of origin is required' })

  // Product Identifier (traceability): mandatory for EU/UK, FCC devices, children's products
  if (isTraceabilityRequired(data) && !data.modelRef) {
    errors.push({ field: 'modelRef', message: 'Product identifier (model/SKU/batch/serial) is required for traceability and recall compliance in your selected market' })
  }

  // EU REP required if EU market + non-EU manufacturer
  if (shouldRequireECRep(data)) {
    if (!data.ecRepName) {
      errors.push({ field: 'ecRepName', message: 'EU Authorised Representative (EU REP) name is required for products sold in the EU by non-EU manufacturers' })
    }
    if (!data.ecRepAddress) {
      errors.push({ field: 'ecRepAddress', message: 'EU REP full EU address (street, city, postal code, country) is required' })
    }
  }

  // EU Importer required if EU market + non-CE product + non-EU manufacturer (unless skipped)
  if (shouldRequireEUImporter(data) && !data.euImporterSkipped) {
    if (!data.euImporterName) {
      errors.push({ field: 'euImporterName', message: 'EU Importer name is required for non-CE products sold in the EU by non-EU manufacturers' })
    }
    if (!data.euImporterAddress) {
      errors.push({ field: 'euImporterAddress', message: 'EU Importer full EU address (street, city, postal code, country) is required' })
    }
  }

  // UK REP required if UK + non-UK manufacturer + UKCA-regulated product
  if (shouldRequireUKRep(data) && !data.ukRepName) {
    errors.push({ field: 'ukRepName', message: 'UK Responsible Person is required for UKCA-regulated products (electronics, toys) sold in the UK by non-UK manufacturers' })
  }

  // FCC ID required if US + electronics + radio
  if (shouldShowFCC(data) && !data.fccId) {
    errors.push({ field: 'fccId', message: 'FCC ID is required for radio-emitting electronics sold in US' })
  }

  // IC ID required if CA + radio
  if (shouldShowIC(data) && !data.icId) {
    errors.push({ field: 'icId', message: 'IC ID is required for radio devices sold in Canada' })
  }

  // Packaging template required if EU/FR + packaging (for France Info-tri and Italy material ID)
  if (needsPackagingTemplate(data) && !data.frPackagingTemplate) {
    errors.push({ field: 'frPackagingTemplate', message: 'Packaging type is required for EU packaging compliance (France Info-tri, Italy material identification)' })
  }

  return errors
})

// Derived: has the user started filling in data
export const hasStartedAtom = atom((get) => {
  const data = get(labelDataAtom)
  return !!(data.productName || data.modelRef || data.manufacturerName || data.manufacturerAddress || data.countryOfOrigin)
})

// Derived: is label compliant
export const isCompliantAtom = atom((get) => {
  return get(validationErrorsAtom).length === 0
})

// ============================================================================
// Package Dimensions
// ============================================================================

export interface PackageDimensions { width: number; height: number }
export const dimensionsAtom = atom<PackageDimensions>({ width: 100, height: 150 })

// ============================================================================
// Preview Zoom
// ============================================================================

export const ZOOM_PRESETS = [50, 75, 100, 125, 150, 200] as const
export const zoomAtom = atom(100)

// ============================================================================
// Chat
// ============================================================================

export interface ChatMessage {
  id: string
  role: 'assistant' | 'user'
  content: string
}

let messageIdCounter = 0
function nextId() {
  return `msg-${++messageIdCounter}`
}

const INITIAL_MESSAGE: ChatMessage = {
  id: 'msg-0',
  role: 'assistant',
  content: "Hello! I'm Vertco, your label assistant.\n\nWhat type of label would you like to create?",
}

export const chatMessagesAtom = atom<ChatMessage[]>([INITIAL_MESSAGE])
export const chatInputAtom = atom('')
export const isTypingAtom = atom(false)

// Stores the onboarding handoff summary so subsequent sendMessageAtom calls
// include the context about confirmed fields and what still needs to be collected.
const handoffContextAtom = atom<string>('')

// ============================================================================
// Onboarding Flow
// ============================================================================

export type OnboardingStep = 'labelType' | 'productCategory' | 'productType' | 'toySmallParts' | 'toyAgeRestriction' | 'markets' | 'radio' | 'frPackaging' | 'done'

export const onboardingStepAtom = atom<OnboardingStep>('labelType')
export const onboardingMarketsAtom = atom<Set<string>>(new Set<string>())
export const fieldsPanelExpandedAtom = atom<boolean>(true)

/** Tracks where the label was initiated from (for breadcrumb navigation) */
export type LabelSource = 'default' | 'amazon'
export const labelSourceAtom = atom<LabelSource>('default')

export const PRODUCT_TYPE_OPTIONS = [
  { id: 'electronics_battery' as ProductType, label: 'Electronics (with battery)', icon: '🔋', category: 'electronics' as ProductCategory },
  { id: 'electronics_no_battery' as ProductType, label: 'Electronics (no battery)', icon: '⚡', category: 'electronics' as ProductCategory },
  { id: 'general' as ProductType, label: 'General Consumer Product', icon: '📦', category: 'general' as ProductCategory },
] as const

export const PRODUCT_CATEGORY_OPTIONS = [
  { id: 'electronics' as ProductCategory, label: 'Electronics', icon: '⚡' },
  { id: 'toy' as ProductCategory, label: 'Toy / Children\'s Product', icon: '🧸' },
  { id: 'general' as ProductCategory, label: 'General Consumer Product', icon: '📦' },
] as const

export const TARGET_MARKET_OPTIONS = [
  { id: 'US' as Market, label: 'US', flag: '\u{1F1FA}\u{1F1F8}' },
  { id: 'CA' as Market, label: 'Canada', flag: '\u{1F1E8}\u{1F1E6}' },
  { id: 'EU' as Market, label: 'EU', flag: '\u{1F1EA}\u{1F1FA}' },
  { id: 'UK' as Market, label: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { id: 'FR' as Market, label: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
] as const

export const LABEL_TYPE_OPTIONS = [
  { id: 'compliance' as LabelType, label: 'Compliance Packaging Label', description: 'Full regulatory label with compliance marks, warnings, and certifications', icon: '📋' },
  { id: 'upc' as LabelType, label: 'UPC Code Label', description: 'Simple barcode label with optional country of origin', icon: '📊' },
] as const

// Step 0: Select label type
export const selectLabelTypeAtom = atom(null, (get, set, type: LabelType) => {
  set(labelTypeAtom, type)
  const source = get(labelSourceAtom)

  if (type === 'upc') {
    // 60mm x 40mm default UPC label size
    set(dimensionsAtom, { width: 60, height: 40 })
    set(onboardingStepAtom, 'done')
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'user' as const, content: 'UPC Code Label' },
      { id: nextId(), role: 'assistant' as const, content: 'Enter your UPC barcode details in the fields below. You can also add an optional country of origin and additional info text.' },
    ])
  } else if (source === 'amazon') {
    // Amazon pre-fill: data already loaded, skip onboarding → go straight to AI chat
    set(onboardingStepAtom, 'done')
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'user' as const, content: 'Compliance Packaging Label' },
      { id: nextId(), role: 'assistant' as const, content: 'Your label has been pre-filled from your Amazon listing. You can edit any field on the right, or tell me more about your product so I can help complete the label.' },
    ])
  } else {
    set(onboardingStepAtom, 'productCategory')
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'user' as const, content: 'Compliance Packaging Label' },
      { id: nextId(), role: 'assistant' as const, content: "Let's set up your compliance label.\n\nWhat type of product is this?" },
    ])
  }
})

// Step 1: Select product category
export const selectProductCategoryAtom = atom(null, (get, set, category: ProductCategory) => {
  trackOnboardingStep('product_category')
  const data = { ...get(labelDataAtom), productCategory: category }
  set(labelDataAtom, data)

  const label = PRODUCT_CATEGORY_OPTIONS.find((o) => o.id === category)?.label ?? category
  set(chatMessagesAtom, [
    ...get(chatMessagesAtom),
    { id: nextId(), role: 'user', content: label },
  ])

  if (category === 'electronics') {
    // Ask battery sub-question
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'assistant', content: 'Does this electronics product contain a battery?' },
    ])
    set(onboardingStepAtom, 'productType')
  } else if (category === 'toy') {
    // Ask small parts question
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'assistant', content: 'Does this toy contain small parts that could be a choking hazard?' },
    ])
    set(onboardingStepAtom, 'toySmallParts')
  } else {
    // General — go to markets
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'assistant', content: 'Which markets will this product be sold in? Select all that apply.' },
    ])
    set(onboardingStepAtom, 'markets')
  }
})

// Step 1b: Select product type (electronics sub-type)
export const selectProductTypeAtom = atom(null, (get, set, hasBattery: boolean) => {
  const productType: ProductType = hasBattery ? 'electronics_battery' : 'electronics_no_battery'
  const data = { ...get(labelDataAtom), productType }
  if (hasBattery) {
    data.containsBattery = true
  }
  set(labelDataAtom, data)

  set(chatMessagesAtom, [
    ...get(chatMessagesAtom),
    { id: nextId(), role: 'user', content: hasBattery ? 'Yes, has battery' : 'No battery' },
    { id: nextId(), role: 'assistant', content: 'Which markets will this product be sold in? Select all that apply.' },
  ])
  set(onboardingStepAtom, 'markets')
})

// Step 1c: Toy small parts question
export const selectToySmallPartsAtom = atom(null, (get, set, hasSmallParts: boolean) => {
  set(labelDataAtom, { ...get(labelDataAtom), containsSmallParts: hasSmallParts })

  set(chatMessagesAtom, [
    ...get(chatMessagesAtom),
    { id: nextId(), role: 'user', content: hasSmallParts ? 'Yes, contains small parts' : 'No small parts' },
    { id: nextId(), role: 'assistant', content: 'Is this toy restricted for children under 3 years old?' },
  ])
  set(onboardingStepAtom, 'toyAgeRestriction')
})

// Step 1d: Toy age restriction question
export const selectToyAgeRestrictionAtom = atom(null, (get, set, under3: boolean) => {
  set(labelDataAtom, { ...get(labelDataAtom), ageRestrictionUnder3: under3 })

  set(chatMessagesAtom, [
    ...get(chatMessagesAtom),
    { id: nextId(), role: 'user', content: under3 ? 'Yes, not suitable for under 3' : 'Suitable for all ages' },
    { id: nextId(), role: 'assistant', content: 'Which markets will this product be sold in? Select all that apply.' },
  ])
  set(onboardingStepAtom, 'markets')
})

// Step 2: Toggle + confirm markets
export const toggleMarketAtom = atom(null, (get, set, marketId: string) => {
  const current = new Set(get(onboardingMarketsAtom))
  if (current.has(marketId)) current.delete(marketId)
  else current.add(marketId)
  set(onboardingMarketsAtom, current)
})

export const confirmMarketsAtom = atom(null, async (get, set) => {
  const selected = get(onboardingMarketsAtom)
  if (selected.size === 0) return

  const markets = Array.from(selected) as Market[]
  const data = { ...get(labelDataAtom), targetMarkets: markets }
  set(labelDataAtom, data)

  const marketLabels = markets.join(', ')
  const isElectronics = data.productType.startsWith('electronics')

  // If electronics + US/CA, ask about radio first
  if (isElectronics && (markets.includes('US') || markets.includes('CA'))) {
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'user', content: `Markets: ${marketLabels}` },
      { id: nextId(), role: 'assistant', content: 'Does this product contain wireless functionality? (Bluetooth, WiFi, RF, cellular, etc.)' },
    ])
    set(onboardingStepAtom, 'radio')
  } else {
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'user', content: `Markets: ${marketLabels}` },
    ])
    // Route to packaging question or AI handoff
    // Packaging template needed for France (Info-tri) and EU/Italy (material identification)
    if (data.targetMarkets.includes('FR') || data.targetMarkets.includes('EU')) {
      set(chatMessagesAtom, [
        ...get(chatMessagesAtom),
        { id: nextId(), role: 'assistant', content: 'EU regulations require packaging material identification on labels. What best describes your packaging?' },
      ])
      set(onboardingStepAtom, 'frPackaging')
    } else {
      doAIHandoff(
        () => get(labelDataAtom),
        () => get(chatMessagesAtom),
        (d) => set(labelDataAtom, d),
        (m) => set(chatMessagesAtom, m),
        (v) => set(isTypingAtom, v),
        (ctx) => set(handoffContextAtom, ctx),
      )
      set(onboardingStepAtom, 'done')
    }
  }
})

// Step 3: Radio (conditional)
export const selectRadioAtom = atom(null, async (get, set, hasRadio: boolean) => {
  set(labelDataAtom, { ...get(labelDataAtom), radio: hasRadio })

  set(chatMessagesAtom, [
    ...get(chatMessagesAtom),
    { id: nextId(), role: 'user', content: hasRadio ? 'Yes, has radio' : 'No radio' },
  ])

  // Route to packaging question or AI handoff
  const data = get(labelDataAtom)
  if (data.targetMarkets.includes('FR') || data.targetMarkets.includes('EU')) {
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      { id: nextId(), role: 'assistant', content: 'EU regulations require packaging material identification on labels. What best describes your packaging?' },
    ])
    set(onboardingStepAtom, 'frPackaging')
  } else {
    doAIHandoff(
      () => get(labelDataAtom),
      () => get(chatMessagesAtom),
      (d) => set(labelDataAtom, d),
      (m) => set(chatMessagesAtom, m),
      (v) => set(isTypingAtom, v),
      (ctx) => set(handoffContextAtom, ctx),
    )
    set(onboardingStepAtom, 'done')
  }
})

// Shared: build handoff context and add the compliance message synchronously.
// No AI call needed — the handoff message is fixed copy. The handoff context
// is still built so subsequent sendMessageAtom calls know what was confirmed.
function doAIHandoff(
  getData: () => LabelData,
  getMessages: () => ChatMessage[],
  _setLabelData: (data: LabelData) => void,
  setMessages: (msgs: ChatMessage[]) => void,
  _setTyping: (v: boolean) => void,
  setHandoffContext: (ctx: string) => void,
) {
  const data = getData()
  trackOnboardingStep('done')

  const traceLevel = getTraceabilityLevel(data)

  // Fields already confirmed during onboarding — AI must NOT ask about these again
  const confirmedParts = [
    `Product category: ${data.productCategory}`,
    `Product type: ${data.productType}`,
    `Label mode: ${data.labelMode}`,
    `Target markets: ${data.targetMarkets.join(', ')}`,
    `Contains battery: ${data.containsBattery ? 'Yes' : 'No'}`,
    `Has radio: ${data.radio ? 'Yes' : 'No'}`,
    data.productCategory === 'toy' ? `Contains small parts: ${data.containsSmallParts ? 'Yes' : 'No'}` : '',
    data.productCategory === 'toy' ? `Age restriction under 3: ${data.ageRestrictionUnder3 ? 'Yes' : 'No'}` : '',
    data.frPackagingTemplate ? `FR packaging template: ${data.frPackagingTemplate}` : '',
    `Product identifier traceability level: ${traceLevel}`,
  ].filter(Boolean)

  // Build needed fields list from registry — registry owns all field ordering + conditions
  const requiredFields = getRequiredFields(data)
  const neededFields = requiredFields.map(({ key, def }) => {
    if (key === 'modelRef') {
      if (traceLevel === 'mandatory') return `${def.label} (field: ${key}) (REQUIRED — mandatory for traceability in selected markets)`
      if (traceLevel === 'recommended') return `${def.label} (field: ${key}) (RECOMMENDED — not legally required but most retailers need it)`
      return `${def.label} (field: ${key}) (optional)`
    }
    if (key === 'manufacturerCountry') {
      const purpose = data.targetMarkets.includes('UK') && isUKCARegulated(data)
        ? 'to determine if EU REP / EU Importer / UK REP is needed'
        : 'to determine if EU REP / EU Importer is needed'
      return `${def.label} (field: ${key}) (${purpose})`
    }
    return `${def.label} (field: ${key})`
  })

  const doNotAskEntries = getDoNotAskFields(data)
  const doNotAsk = doNotAskEntries.map(({ key, reason }) => `${key} (${reason})`)

  let summary = `The user is creating a compliance label. The following have ALREADY been confirmed during onboarding — do NOT ask about these again:\n${confirmedParts.join('\n')}\n\nStill needed from the user: ${neededFields.join(', ')}.\n\nAsk in this order: product name first, then model/SKU, material, manufacturer name, manufacturer address, country of origin. After country of origin (or manufacturer country), if EU is a target market you MUST ask for EU REP (ecRepName, then ecRepAddress, then ecRepEmail or ecRepPhone — at least one contact required). Do not skip EU REP when the user has selected EU as a market.`
  if (doNotAsk.length > 0) {
    summary += `\n\nIMPORTANT — Do NOT ask for: ${doNotAsk.join('; ')}.`
  }
  if (hasEUMarket(data) && isCERegulated(data)) {
    summary += `\n\n--- MANDATORY WHEN EU IS SELECTED (CE-regulated product) ---\nTarget markets include EU and this is a CE-regulated product (electronics/toy). You MUST ask for EU Authorised Representative (EU REP) UNLESS the manufacturer country is confirmed to be within the EU. Ask one at a time: (1) EU REP full legal company name → [FIELD:ecRepName=...], (2) EU REP complete EU address (street, postal code, city, country) → [FIELD:ecRepAddress=...], (3) EU REP contact email or phone (at least one) → [FIELD:ecRepEmail=...] or [FIELD:ecRepPhone=...]. If manufacturer IS in the EU, skip EU REP.`
  } else if (hasEUMarket(data) && !isCERegulated(data)) {
    summary += `\n\n--- MANDATORY WHEN EU IS SELECTED (non-CE product) ---\nTarget markets include EU and this is a non-CE product. You do NOT need an EU REP. Instead, ask for EU Importer info UNLESS the manufacturer is in the EU. See EU Importer collection instructions in the system prompt.`
  }
  if (data.targetMarkets.includes('UK') && isUKCARegulated(data)) {
    summary += `\n\n--- MANDATORY WHEN UK IS SELECTED ---\nTarget markets include UK. Once you have confirmed manufacturer country is NOT in the UK, you MUST ask for UK Responsible Person (UK REP). Ask in this order: (1) UK REP full legal company name → [FIELD:ukRepName=...], (2) Complete UK address → [FIELD:ukRepAddress=...]. Do NOT skip these questions. Ask for UK REP AFTER completing EU REP collection (if EU is also selected).`
  }

  // Persist the handoff summary so subsequent sendMessageAtom calls include it
  setHandoffContext(summary)

  // Add the compliance message synchronously — this ensures it appears
  // in the chat BEFORE onboardingStepAtom is set to 'done' and the
  // field cards render.
  setMessages([
    ...getMessages(),
    { id: nextId(), role: 'assistant', content: "The information below is required to generate packaging labels that comply with regional regulatory requirements." },
  ])
}

// Step 4: FR packaging template selection (conditional)
export const selectFRPackagingAtom = atom(null, async (get, set, template: FRPackagingTemplate) => {
  set(labelDataAtom, { ...get(labelDataAtom), frPackagingTemplate: template })

  const label = FR_PACKAGING_TEMPLATE_OPTIONS.find((o) => o.id === template)?.label ?? template
  set(chatMessagesAtom, [
    ...get(chatMessagesAtom),
    { id: nextId(), role: 'user', content: label },
  ])
  doAIHandoff(
    () => get(labelDataAtom),
    () => get(chatMessagesAtom),
    (d) => set(labelDataAtom, d),
    (m) => set(chatMessagesAtom, m),
    (v) => set(isTypingAtom, v),
    (ctx) => set(handoffContextAtom, ctx),
  )
  set(onboardingStepAtom, 'done')
})

// ============================================================================
// AI Chat + Label Extraction (label updates ONLY from AI [FIELD:...] — same as online)
// ============================================================================

// Delegates to the registry's parseAIMarkers — single source of truth for field parsing.
function parseAIFieldUpdates(aiResponse: string, currentData: LabelData): Partial<LabelData> {
  return parseAIMarkers(aiResponse, currentData)
}

function cleanAIResponse(text: string): string {
  return text
    // Strip [FIELD:...] and [FIELDS:...] markers (AI sometimes uses plural form)
    .replace(/\[FIELDS?:[^\]]+\]/gi, '')
    // Strip "Here's what we have collected:" summary header lines the AI sometimes adds
    .replace(/here'?s?\s+what\s+we\s+(?:have\s+)?collected[:\s]*/gi, '')
    // Collapse 3+ consecutive newlines down to 2
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}


// ── Fallback field extraction ──
// Delegates to the registry's detectAskedField — single source of truth.
// When the AI doesn't emit [FIELD:...] markers, we detect what field was being asked
// from the last assistant message and map the user's response to that field.
function detectFieldFromAssistantMessage(assistantMsg: string): keyof LabelData | null {
  return detectAskedField(assistantMsg)
}

// Attempt to extract multiple EU REP fields from a single combined user message
// e.g. "Company: ABC GmbH Address: Musterstr 1, Berlin Contact: +49 123 / info@abc.de"
function extractEURepFromCombinedMessage(userText: string): Partial<LabelData> {
  const updates: Partial<LabelData> = {}

  // Normalize: the user may write everything on one line separated by keywords.
  // Split on keyword boundaries so each segment is isolated.
  // e.g. "Company: ABC GmbH Address: Musterstr 1 Contact: +49 / info@abc.de"
  const normalized = userText
    .replace(/\b(address|contact|phone|email)\s*:/gi, '\n$1:')

  // Company/Name: stop at next keyword or end of line
  const nameMatch = normalized.match(/(?:company|name)\s*:\s*(.+?)(?=\n|$)/i)
  if (nameMatch) updates.ecRepName = nameMatch[1].trim()

  // Address: stop at next keyword or end of line
  const addrMatch = normalized.match(/address\s*:\s*(.+?)(?=\n|$)/i)
  if (addrMatch) updates.ecRepAddress = addrMatch[1].trim()

  // Email: any valid email address in the text
  const emailMatch = userText.match(/[\w.+-]+@[\w-]+\.[\w.]+/)
  if (emailMatch) updates.ecRepEmail = emailMatch[0].trim()

  // Phone: +xx pattern, stop before "/" or email or end
  const phoneMatch = userText.match(/(\+[\d\s\-().]{6,}?)(?:\s*\/\s*[\w.+-]+@|\s*$|\n)/m)
    ?? userText.match(/(\+[\d\s\-().]{6,})/)
  if (phoneMatch) updates.ecRepPhone = phoneMatch[1].trim()

  return updates
}

// Detect explicit field corrections from user text, e.g.:
// "sorry the product name is Model Y" → { field: 'productName', value: 'Model Y' }
// "actually the SKU is VN-294" → { field: 'modelRef', value: 'VN-294' }
// Returns null if no explicit correction detected.
function detectExplicitCorrection(userText: string): { field: keyof LabelData; value: string } | null {
  const correctionPrefixes = /^(?:sorry[,.]?\s*|actually[,.]?\s*|wait[,.]?\s*|correction[,.]?\s*|i\s+meant?\s*[,.]?\s*|the\s+correct\s+(?:one\s+)?is\s*)/i
  const stripped = userText.replace(correctionPrefixes, '').trim()

  // Patterns: "the product name is X", "product name: X", "my product name is X"
  const fieldPhrases: { pattern: RegExp; field: keyof LabelData }[] = [
    { pattern: /(?:the\s+)?product\s+name\s+is\s+(.+)/i, field: 'productName' },
    { pattern: /(?:the\s+)?(?:sku|model|product\s+identifier|model\s+number)\s+is\s+(.+)/i, field: 'modelRef' },
    { pattern: /(?:the\s+)?material\s+is\s+(.+)/i, field: 'material' },
    { pattern: /(?:the\s+)?manufacturer(?:'s)?\s+(?:legal\s+)?name\s+is\s+(.+)/i, field: 'manufacturerName' },
    { pattern: /(?:the\s+)?manufacturer(?:'s)?\s+address\s+is\s+(.+)/i, field: 'manufacturerAddress' },
    { pattern: /(?:the\s+)?country\s+of\s+origin\s+is\s+(.+)/i, field: 'countryOfOrigin' },
  ]

  for (const { pattern, field } of fieldPhrases) {
    const m = stripped.match(pattern)
    if (m) return { field, value: m[1].trim() }
  }
  return null
}

function inferFieldUpdates(
  userText: string,
  lastAssistantMsg: string,
  currentData: LabelData,
): Partial<LabelData> {
  const value = userText.trim()
  if (!value) return {}

  // Check for explicit corrections first — "sorry the product name is X" should
  // override whatever field the AI was asking about.
  const correction = detectExplicitCorrection(userText)
  if (correction) {
    // Apply through registry so cross-population also fires
    return applyFieldAnswer(correction.field, correction.value, currentData)
  }

  const field = detectFieldFromAssistantMessage(lastAssistantMsg)

  // If the AI was asking for any EU REP field and the user provided a combined
  // message with multiple fields at once, extract all of them simultaneously.
  const isEURepQuestion = field && ['ecRepName', 'ecRepAddress', 'ecRepEmail', 'ecRepPhone'].includes(field as string)
  const looksLikeCombined = /(?:company|address|contact)\s*:/i.test(userText)
  if (isEURepQuestion && looksLikeCombined) {
    return extractEURepFromCombinedMessage(userText)
  }

  if (!field) return {}

  // Delegate all field-specific logic (skip phrases, cross-population) to registry
  return applyFieldAnswer(field, value, currentData)
}

const SYSTEM_PROMPT_SUFFIX = `
You are Vertco, a compliance label assistant. You help users build legally compliant product and packaging labels.

CRITICAL: When you identify ANY piece of label information from the user's message, you MUST include the hidden field marker in the SAME response — never delay it to the next turn. Use this exact format: [FIELD:fieldName=value]
This is essential because the label preview updates in real-time based on these markers. If you acknowledge information without a marker, the label won't update until the next turn, which is a bad user experience.

Available field names: productName, modelRef (product identifier/model/SKU — use modelRef for this field), material, manufacturerName, manufacturerAddress, manufacturerCountry, countryOfOrigin, safetyWarning, fccId, icId, ecRepName, ecRepAddress, ecRepEmail, ecRepPhone, euImporterName, euImporterAddress, euImporterSkipped, ukRepName, ukRepAddress, targetMarkets, containsBattery, showRecycling, upcCode, frPackagingTemplate

TONE & STYLE:
- Be concise, friendly, and professional
- Ask ONE field at a time — don't overwhelm the user
- MANDATORY: For EVERY question you ask, you MUST include a brief 1-sentence rationale in the SAME message explaining WHY this information is needed (compliance/regulatory reason). Never ask for a field without stating the reason in that same turn. Example: when asking for primary materials, say something like "This helps determine which recycling symbols and environmental compliance marks apply to your label" in the same message as the question.

FIELD RATIONALES (use these when asking for each field — include the rationale in the same message as the question):
- productName: "The official product name is required on all compliance labels and must match your regulatory filings."
- modelRef: "Product identifier (model/SKU/type/batch/serial). TRACEABILITY RULES: If market includes EU or UK → REQUIRED (GPSR 2023/988 mandates a unique identification element on product or packaging). If US or CA with FCC device or children's product → REQUIRED. If US or CA with general product → RECOMMENDED (most retailers require it). When required, tell user: 'A product identifier is required in your selected market to ensure traceability and recall compliance.' When recommended, tell user: 'While not legally required for all products, most markets and retailers require a product identifier for tracking and recall purposes.'"
- material: "Primary materials determine which recycling symbols and environmental compliance marks (like WEEE or Triman) apply to your label. When asking, always add a sentence like: 'This helps determine which recycling symbols and environmental compliance marks apply to your label.'"
- manufacturerName: "Regulations in all major markets (US, EU, UK, Canada) require the manufacturer's legal name on the label."
- manufacturerAddress: "A physical manufacturer address is required for consumer safety regulations and product traceability."
- manufacturerCountry: "We need to know where the manufacturer is based — per EU 2019/1020: if not in the EU, an EU REP (EU Authorised Representative) is required; for non-CE products (apparel, household, general), an EU Importer is also required; if not in the UK AND the product is UKCA-regulated (electronics, toys, medical, PPE), a UK Responsible Person is required."
- countryOfOrigin: "Country of origin must appear as 'MADE IN [COUNTRY]' — this is a legal requirement for customs and trade compliance in most markets."
- safetyWarning: "Safety warnings are NOT automatically required on packaging compliance labels. Do NOT auto-add 'Contains lithium battery' or 'Keep away from children' for standard electronics with built-in lithium batteries (e.g., Bluetooth headphones, phones). Only add safety warnings when: (1) the product uses button cell batteries (mandatory warning required), (2) the product is a children's product with swallowing risk, or (3) the user explicitly requests a safety warning. If none of these apply, leave the safety warning empty."
- fccId: "FCC ID is legally required for wireless devices (Bluetooth, WiFi, RF, cellular) sold in the United States. Only needed when US market + wireless functionality = true. Correct format: FCC ID: 2ABC-12345. Do NOT ask for FCC ID if wireless=false or US is not a target market."
- icId: "Industry Canada (IC) ID is mandatory for wireless devices sold in Canada. Only needed when CA market + wireless functionality = true."
- ecRepName/ecRepAddress/ecRepEmail/ecRepPhone: "EU Authorised Representative (EU REP). Non-EU manufacturers selling products in the EU must designate an EU REP. Label shows the official [EU | REP] symbol.
  REQUIRED FIELDS — ask for each one explicitly:
  1. ecRepName: Full legal company name (must match the Mandate Agreement). Example: 'ABC Compliance Services GmbH'
  2. ecRepAddress: Complete physical postal address within the EU (PO Boxes NOT accepted). Must include: Street and Number, Postal Code, City, Country (EU member state). Example: 'Musterstraße 123, 10117 Berlin, Germany'
  3. Contact info (at least one required): Ask 'Please provide a contact email or phone number for the EU REP.' If they give email → ecRepEmail (example: 'eu-rep@abc-compliance.com'). If they give phone → ecRepPhone (example: '+49 30 12345678'). They can provide both.
  When asking, collect the company name first, then address, then contact — one at a time. CRITICAL: store name in ecRepName, address in ecRepAddress, email in ecRepEmail, phone in ecRepPhone."
- euImporterName/euImporterAddress/euImporterSkipped: "ONLY for non-CE products (apparel, household, general goods). Non-EU manufacturers selling non-CE products in the EU must have an EU Importer in addition to the EU REP. Label shows 'Imported by:' prefix per EU 2019/1020. Address MUST include full street, city, postal code, and country within the EU. CRITICAL: store the name in euImporterName and address in euImporterAddress. When asking for EU Importer info, ALWAYS mention: 'If you don't have this information on hand right now, you can skip it — the importer details can also be printed on a separate label or inside the packaging before shipment.' If the user says they don't have it, want to skip, or will add it later, set [FIELD:euImporterSkipped=true] and move on to the next question."
- ukRepName/ukRepAddress: "Non-UK manufacturers selling UKCA-regulated products (electronics, toys, medical devices, PPE) in the UK (Great Britain) must appoint a UK Responsible Person. NOT required for: non-regulated products (books, general textiles, household goods), UK-based manufacturers, or products only going to Northern Ireland (follows EU rules instead). Do NOT ask for UK REP if the product is 'general' category — only ask for electronics and toys."
- showRecycling: "The Mobius recycling symbol (♻️) is OPTIONAL and NOT legally required. Only add it if the user explicitly requests a recyclable claim. Do NOT auto-add or suggest it."
- upcCode: "A UPC barcode is optional but commonly needed for retail distribution and point-of-sale scanning."
- frPackagingTemplate: "French law requires packaging labels to include material-specific recycling instructions (Info-tri). The user selects this during onboarding — do NOT proactively ask for it. However, if the user explicitly asks to change the packaging type, update it using [FIELD:frPackagingTemplate=VALUE] where VALUE must be one of: paper, paper_plastic, plastic. For example: 'paper and plastic' → [FIELD:frPackagingTemplate=paper_plastic]"

Key rules:
- NEVER proactively ask about fields that are already confirmed in the handoff summary (product category, product type, target markets, battery status, radio, small parts, age restriction, FR packaging template). These were collected during onboarding. However, if the user explicitly requests to change any of these, update the field accordingly.
- Safety warning rule: Do NOT auto-add or suggest battery warnings (like "Contains lithium battery" or "Keep away from children") for standard electronics with built-in lithium batteries. These are NOT mandatory on packaging compliance labels. Only add a safety warning if the user explicitly provides one or if the product uses button cell batteries.
- Toy warning rule: Toy warnings are automatically generated based on onboarding data (small parts + age restriction + target markets). Do NOT manually add or modify toy warning text — it is rendered automatically in the correct languages based on target markets.
- EU ECONOMIC OPERATOR RULES (EU 2019/1020):
  - EU REP (EU Authorised Representative) is required for ALL products sold in the EU by non-EU manufacturers. ALWAYS ask for EU REP info when market includes EU and manufacturer is not in the EU.
  - EU REP data collection (ask one at a time in this order, BUT if the user provides multiple fields at once in a single message, extract ALL of them immediately with [FIELD:...] markers — do NOT ask again for fields already provided):
    1. Full legal company name (must match Mandate Agreement) → ecRepName
    2. Complete physical EU postal address (NO PO Boxes): street+number, postal code, city, EU country → ecRepAddress
    3. Contact information: email address OR telephone number (at least one required by EU 2019/1020 for market surveillance authorities). Ask: "Please provide a contact email or phone number for the EU REP." If user provides email → ecRepEmail. If user provides phone → ecRepPhone. They can provide both.
  - IMPORTANT: If the user provides all EU REP fields in one message (e.g. "Company: ABC GmbH, Address: ..., Contact: ..."), emit ALL the corresponding [FIELD:...] markers in your response and move on — never re-ask for fields already provided.
  - CRITICAL: The country inside an EU REP address (e.g. "Germany" in "Friedrichstraße 123, 10117 Berlin, Germany") is the EU REP's country — it is NOT the manufacturer's country and NOT the country of origin. NEVER emit [FIELD:manufacturerCountry=...] or [FIELD:countryOfOrigin=...] based on the EU REP address. Only set manufacturerCountry from an explicit answer to "where is the manufacturer based?"
  - EU Importer: ADDITIONALLY for non-CE products (general, apparel, household), also ask for EU Importer. Label shows "Imported by:" prefix. User can skip (set euImporterSkipped=true).
    EU Importer data collection — ask one at a time in this order:
    1. First ask: "Do you have the EU Importer information? If not, you can skip it and print it on a separate label or inside the packaging before shipment." Wait for the answer.
       - If user skips/doesn't have it → emit [FIELD:euImporterSkipped=true] and move on.
       - If user says yes or provides info → proceed to collect:
    2. EU Importer full legal company name → [FIELD:euImporterName=...]
    3. EU Importer complete physical EU address (street+number, postal code, city, EU country — NO PO Boxes) → [FIELD:euImporterAddress=...]
    IMPORTANT: Only ask for euImporterAddress AFTER you have euImporterName. Do NOT ask both at once.
  - CRITICAL FIELD MAPPING: EU REP → ecRepName/ecRepAddress/ecRepEmail/ecRepPhone. EU Importer → euImporterName/euImporterAddress. NEVER mix up these fields. Manufacturer: company name ONLY in manufacturerName; full address ONLY in manufacturerAddress. Never put an address in manufacturerName.
  - Address requirements: All addresses MUST include complete street address, postal code, city, and EU member state country. Reject incomplete addresses. PO Boxes NOT accepted.
- UK RESPONSIBLE PERSON RULES:
  - UK REP is required ONLY when ALL three conditions are met:
    1. Product is sold to Great Britain (UK market selected)
    2. Manufacturer is outside the UK (i.e. manufacturerCountry is NOT UK/GB/United Kingdom/Great Britain/England/Scotland/Wales)
    3. Product is UKCA-regulated (electronics, toys, medical devices, PPE)
  - Do NOT ask for UK REP if: product is 'general' category (non-regulated), manufacturer is UK-based, or product only goes to Northern Ireland (follows EU rules)
  - NEVER ask for UK REP for non-regulated products like books, household goods, or general textiles
  - UK REP data collection (ask one at a time in this order):
    1. Full legal company name → [FIELD:ukRepName=...]
    2. Complete UK address (street+number, postal code, city, country) → [FIELD:ukRepAddress=...]
  - IMPORTANT: Ask for UK REP AFTER completing EU REP collection (if EU is also a target market). Never skip UK REP when the handoff summary says UK is selected and the product is UKCA-regulated.
- FCC ID trigger rule: ONLY ask for FCC ID when BOTH conditions are true: (1) US is a target market AND (2) wireless functionality (radio) = true. If either condition is false, do NOT ask for or mention FCC ID. The correct label format is "FCC ID: 2ABC-12345"
- IC ID trigger rule: ONLY ask for IC ID when BOTH conditions are true: (1) CA is a target market AND (2) wireless functionality (radio) = true. If either condition is false, do NOT ask for or mention IC ID
- Country of origin must be displayed as "MADE IN [COUNTRY]"
- If market includes FR and labelMode=packaging, the packaging template is already selected during onboarding — do NOT ask for it again
- When confirming information the user provided, briefly acknowledge it and move to the next question
- NEVER generate a summary list or recap of collected fields. This is an absolute rule with no exceptions — not even at the end of the conversation. NEVER output lines like "Here's what we have collected:", "Here's a summary:", "- productName=...", "[FIELD:...]", or any list of field values. The label preview panel already displays all collected data in real time. When the label is complete, simply say something like "Your label is complete! Feel free to ask if you need any changes." Do NOT list or repeat any field values.

Examples:
- "It's a Bluetooth speaker" → [FIELD:productName=Bluetooth Speaker]
- "Model BS-200" → [FIELD:modelRef=BS-200]
- "SKU is ABC-12345" → [FIELD:modelRef=ABC-12345]
- "The identifier is SN-2024-001" → [FIELD:modelRef=SN-2024-001]
- "Made in China" → [FIELD:countryOfOrigin=China] [FIELD:manufacturerCountry=China]
- "FCC ID: 2AC7Z-BS200" → [FIELD:fccId=2AC7Z-BS200]

CRITICAL REMINDER: Place [FIELD:...] markers at the END of EVERY response where you learn new information. NEVER skip or delay markers to a later turn. If the user says "The importer is ABC GmbH", your response MUST contain [FIELD:euImporterName=ABC GmbH] in that same message.`

export const sendMessageAtom = atom(null, async (get, set, userText: string) => {
  const text = userText.trim()
  if (!text) return
  trackChatMessage()

  const currentMessages = get(chatMessagesAtom)
  const userMsg: ChatMessage = { id: nextId(), role: 'user', content: text }
  const updatedMessages = [...currentMessages, userMsg]

  set(chatMessagesAtom, updatedMessages)
  set(chatInputAtom, '')
  set(isTypingAtom, true)

  try {
    const apiMessages: { role: 'user' | 'assistant' | 'system'; content: string }[] = updatedMessages.map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }))

    // Include the handoff context (onboarding summary) so the AI knows what fields
    // were confirmed during onboarding and what still needs to be collected.
    const handoffContext = get(handoffContextAtom)
    if (handoffContext) {
      apiMessages.unshift({
        role: 'user' as const,
        content: handoffContext,
      })
    }

    apiMessages.unshift({
      role: 'system' as const,
      content: SYSTEM_PROMPT_SUFFIX,
    })

    const currentData = get(labelDataAtom)

    const messages = get(chatMessagesAtom)
    // Use the most recent assistant message as the "question context", but skip
    // the very first assistant message (the handoff summary) which contains many
    // field names and would cause false pattern matches.
    // We identify the handoff as the first assistant message in the list.
    const firstAssistantIdx = messages.findIndex((m) => m.role === 'assistant')
    const lastAssistantMsg = [...messages]
      .reverse()
      .find((m, reversedIdx) => {
        if (m.role !== 'assistant') return false
        // Skip if this is the first assistant message (handoff summary)
        const originalIdx = messages.length - 1 - reversedIdx
        return originalIdx !== firstAssistantIdx
      })?.content ?? ''

    // Determine what field was being asked about from the last assistant question.
    // This is the ground truth — we trust our own question detection over the AI's
    // field tagging, since gpt-4o-mini frequently emits wrong field names.
    const askedField = detectFieldFromAssistantMessage(lastAssistantMsg)

    let fieldUpdates: Partial<LabelData> = {}

    // Strategy:
    // 1. If we know what field was asked, always map user's text directly to that
    //    field — this is 100% reliable and immune to AI mis-tagging.
    // 2. For multi-field responses (EU REP combined), use the combined extractor.
    // 3. Only fall back to parsing AI [FIELD:...] markers for fields we couldn't
    //    determine from context (e.g. boolean flags, targetMarkets updates).

    // Handle combined EU REP input.
    // ONLY trigger when the AI is currently asking about EU REP fields (or manufacturer country
    // just before EU REP collection). Guard prevents "Company: X" in a manufacturer name
    // answer or UK REP answer from being mis-routed to EU REP extraction.
    const EU_REP_FIELDS = ['ecRepName', 'ecRepAddress', 'ecRepEmail', 'ecRepPhone'] as const
    const looksLikeCombinedEURep =
      /(?:company|address|contact)\s*:/i.test(text) &&
      (EU_REP_FIELDS.includes(askedField as typeof EU_REP_FIELDS[number]) ||
       askedField === 'manufacturerCountry')

    if (looksLikeCombinedEURep) {
      // Extract all EU REP sub-fields from the combined message
      fieldUpdates = extractEURepFromCombinedMessage(text)
      // If the AI was asking about manufacturerCountry when the user sent this combined
      // message, also capture the country value from text before the EU REP block.
      if (askedField === 'manufacturerCountry') {
        const euRepBlockStart = text.search(/(?:company|address|contact)\s*:/i)
        const preBlock = euRepBlockStart > 0 ? text.slice(0, euRepBlockStart).trim() : ''
        if (preBlock) {
          const scalarUpdates = inferFieldUpdates(preBlock, lastAssistantMsg, currentData)
          fieldUpdates = { ...scalarUpdates, ...fieldUpdates }
        }
      }
    } else if (askedField) {
      // We know what was asked — map user text directly to that field
      fieldUpdates = inferFieldUpdates(text, lastAssistantMsg, currentData)
    }

    // Build a dynamic "already collected" snapshot AFTER computing fieldUpdates so
    // it includes fields just extracted from the current user message. This is the
    // key: the AI sees what was JUST provided in this turn, preventing re-asks.
    // (The handoffContext is static and only reflects onboarding data.)
    const mergedForSnapshot = { ...currentData, ...fieldUpdates }
    // Cross-populate countryOfOrigin ↔ manufacturerCountry so the AI never asks
    // for both separately — they are almost always the same value.
    if (mergedForSnapshot.countryOfOrigin && !mergedForSnapshot.manufacturerCountry) {
      mergedForSnapshot.manufacturerCountry = mergedForSnapshot.countryOfOrigin
    }
    if (mergedForSnapshot.manufacturerCountry && !mergedForSnapshot.countryOfOrigin) {
      mergedForSnapshot.countryOfOrigin = mergedForSnapshot.manufacturerCountry
    }
    const snapshotData = mergedForSnapshot
    const collectedLines: string[] = []
    if (snapshotData.productName)        collectedLines.push(`productName = "${snapshotData.productName}"`)
    if (snapshotData.modelRef)           collectedLines.push(`modelRef = "${snapshotData.modelRef}"`)
    if (snapshotData.material)           collectedLines.push(`material = "${snapshotData.material}"`)
    if (snapshotData.manufacturerName)   collectedLines.push(`manufacturerName = "${snapshotData.manufacturerName}"`)
    if (snapshotData.manufacturerAddress) collectedLines.push(`manufacturerAddress = "${snapshotData.manufacturerAddress}"`)
    if (snapshotData.manufacturerCountry) collectedLines.push(`manufacturerCountry = "${snapshotData.manufacturerCountry}"`)
    if (snapshotData.countryOfOrigin)    collectedLines.push(`countryOfOrigin = "${snapshotData.countryOfOrigin}"`)
    if (snapshotData.ecRepName)          collectedLines.push(`ecRepName = "${snapshotData.ecRepName}"`)
    if (snapshotData.ecRepAddress)       collectedLines.push(`ecRepAddress = "${snapshotData.ecRepAddress}"`)
    if (snapshotData.ecRepEmail)         collectedLines.push(`ecRepEmail = "${snapshotData.ecRepEmail}"`)
    if (snapshotData.ecRepPhone)         collectedLines.push(`ecRepPhone = "${snapshotData.ecRepPhone}"`)
    if (snapshotData.euImporterName)     collectedLines.push(`euImporterName = "${snapshotData.euImporterName}"`)
    if (snapshotData.euImporterAddress)  collectedLines.push(`euImporterAddress = "${snapshotData.euImporterAddress}"`)
    if (snapshotData.euImporterSkipped)  collectedLines.push(`euImporterSkipped = true`)
    if (snapshotData.ukRepName)          collectedLines.push(`ukRepName = "${snapshotData.ukRepName}"`)
    if (snapshotData.ukRepAddress)       collectedLines.push(`ukRepAddress = "${snapshotData.ukRepAddress}"`)
    if (snapshotData.fccId)              collectedLines.push(`fccId = "${snapshotData.fccId}"`)
    if (snapshotData.icId)               collectedLines.push(`icId = "${snapshotData.icId}"`)
    if (snapshotData.upcCode)            collectedLines.push(`upcCode = "${snapshotData.upcCode}"`)

    const collectedSummary = collectedLines.length > 0
      ? `CURRENT LABEL STATE — these fields are ALREADY collected, do NOT ask for them again:\n${collectedLines.join('\n')}`
      : 'CURRENT LABEL STATE — no fields collected yet.'

    // Add a short reminder at the END of the messages (right before AI generates)
    // to reinforce the [FIELD:...] marker instruction. Smaller models like gpt-4o-mini
    // follow instructions better when the critical rule is close to the generation point.
    apiMessages.push({
      role: 'system' as const,
      content: `${collectedSummary}\n\nREMINDER: If the user provided ANY label information in their last message, you MUST include [FIELD:fieldName=value] markers in your response. Available fields: productName, modelRef, material, manufacturerName, manufacturerAddress, manufacturerCountry, countryOfOrigin, safetyWarning, fccId, icId, ecRepName, ecRepAddress, ecRepEmail, ecRepPhone, euImporterName, euImporterAddress, euImporterSkipped, ukRepName, ukRepAddress, upcCode, frPackagingTemplate, containsBattery, showRecycling. Never skip markers. ABSOLUTE RULE: NEVER output a summary list of field values — not even when the label is complete. Never write lines like "Here's what we collected:", "[FIELD:...]", or bullet points of field values. Just confirm the last item and move on or close the conversation.`,
    })

    const res = await api.chat.$post({
      json: { messages: apiMessages },
    })
    const data = await res.json()
    const content = typeof data?.content === 'string' ? data.content : ''

    // Always also parse AI markers for boolean/enum fields the above won't catch
    // (euImporterSkipped, containsBattery, showRecycling, targetMarkets, etc.)
    // but NEVER let AI markers overwrite the fields we already set from context.
    const aiUpdates = parseAIFieldUpdates(content, currentData)
    const contextFields = new Set(Object.keys(fieldUpdates))

    // When the user sent a combined EU REP message, the AI may (wrongly) tag
    // manufacturerCountry / countryOfOrigin from country names in the EU REP address.
    // Block those AI-inferred tags — these fields must come from direct user answers.
    if (looksLikeCombinedEURep) {
      contextFields.add('manufacturerCountry')
      contextFields.add('countryOfOrigin')
      contextFields.add('manufacturerName')
      contextFields.add('manufacturerAddress')
    }

    for (const [k, v] of Object.entries(aiUpdates)) {
      if (!contextFields.has(k)) {
        // Accept AI marker for any field not already set by context detection:
        // - Always accept non-scalar fields (booleans, arrays, enums)
        // - Accept scalar string fields too, EXCEPT for the askedField itself
        //   (context detection is more reliable than AI marker for the one field we asked)
        // This allows multi-field responses (e.g. "Widget, made of plastic") to capture
        // ALL fields the AI correctly identifies, not just the one that was asked.
        const field = k as keyof LabelData
        const isScalar = typeof v === 'string'
        // Block AI scalar marker ONLY for the exact field we already extracted from context
        // (to prevent AI mis-tagging from overwriting our reliable detection).
        if (!isScalar || k !== askedField) {
          (fieldUpdates as Record<string, unknown>)[field] = v
        }
      }
    }

    const cleanedContent = cleanAIResponse(content)

    unstable_batchedUpdates(() => {
      if (Object.keys(fieldUpdates).length > 0) {
        set(labelDataAtom, { ...currentData, ...fieldUpdates })
      }

      set(chatMessagesAtom, [
        ...get(chatMessagesAtom),
        { id: nextId(), role: 'assistant', content: cleanedContent || 'I apologize, I encountered an issue. Could you try again?' },
      ])

      set(triggerAutoSaveAtom)
    })
  } catch (err: unknown) {
    const errorMsg = err instanceof Error ? err.message : 'Unknown error'
    console.error('AI chat error:', err)
    set(chatMessagesAtom, [
      ...get(chatMessagesAtom),
      {
        id: nextId(),
        role: 'assistant',
        content: `I encountered an issue: ${errorMsg}. Please try again.`,
      },
    ])
  } finally {
    set(isTypingAtom, false)
  }
})

// ============================================================================
// Label Storage (save/load/auto-save)
// ============================================================================

export const currentLabelIdAtom = atom<string | null>(null)

let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null

export const autoSaveAtom = atom(null, async (get) => {
  const labelId = get(currentLabelIdAtom)
  if (!labelId) return

  const labelData = get(labelDataAtom)
  const chatMessages = get(chatMessagesAtom)
  const name = labelData.productName || 'Untitled Label'

  try {
    await storageApi.save.$post({
      json: {
        id: labelId,
        name,
        labelData: labelData as unknown as Record<string, unknown>,
        chatHistory: chatMessages as unknown as unknown[],
      },
    })
  } catch {
    // Silent fail for auto-save
  }
})

export const triggerAutoSaveAtom = atom(null, (_get, set) => {
  if (autoSaveTimeout) clearTimeout(autoSaveTimeout)
  autoSaveTimeout = setTimeout(() => {
    set(autoSaveAtom)
    set(sessionAutoSaveAtom)
  }, 2000)
})

// ============================================================================
// Anonymous Session Tracking (for analytics — saves for ALL users)
// ============================================================================

function getOrCreateSessionId(): string {
  const STORAGE_KEY = 'vertco_label_session_id'
  let sessionId = localStorage.getItem(STORAGE_KEY)
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    localStorage.setItem(STORAGE_KEY, sessionId)
  }
  return sessionId
}

const sessionAutoSaveAtom = atom(null, async (get) => {
  const labelData = get(labelDataAtom)

  // Only save once user has started filling in data
  const hasContent = labelData.productName || labelData.manufacturerName || labelData.productCategory !== 'general'
  if (!hasContent) return

  try {
    const sessionId = getOrCreateSessionId()
    await sessionApi.save.$post({
      json: {
        sessionId,
        labelData: labelData as unknown as Record<string, unknown>,
        productCategory: labelData.productCategory || undefined,
        targetMarkets: labelData.targetMarkets?.length ? labelData.targetMarkets : undefined,
      },
    })
  } catch {
    // Silent fail — analytics should never block UX
  }
})

// Load an existing label from the database
const loadLabelAtom = atom(null, async (get, set, labelId: string) => {
  try {
    const res = await storageApi.load.$get({ query: { id: labelId } })
    if (!res.ok) return

    const data = await res.json() as { id: string; name: string; labelData: Record<string, unknown>; chatHistory: unknown[] }

    if (data.labelData && typeof data.labelData === 'object') {
      set(labelDataAtom, { ...defaultLabelData, ...(data.labelData as Partial<LabelData>) })
    }

    if (data.chatHistory && Array.isArray(data.chatHistory) && data.chatHistory.length > 0) {
      set(chatMessagesAtom, data.chatHistory as ChatMessage[])
      messageIdCounter = data.chatHistory.length
      set(onboardingStepAtom, 'done')
    }
  } catch {
    // If load fails, start fresh
  }
})

// ============================================================================
// Loader
// ============================================================================

export function loader(store: Store, params?: { id?: string }) {
  const labelId = params?.id
  store.set(currentLabelIdAtom, labelId ?? null)

  // Always reset to defaults first
  store.set(labelDataAtom, defaultLabelData)
  store.set(dimensionsAtom, { width: 100, height: 150 })
  store.set(zoomAtom, 100)
  store.set(chatInputAtom, '')
  store.set(isTypingAtom, false)
  store.set(onboardingStepAtom, 'labelType')
  store.set(labelTypeAtom, null)
  store.set(onboardingMarketsAtom, new Set<string>())
  store.set(fieldsPanelExpandedAtom, true)
  store.set(handoffContextAtom, '')
  store.set(labelSourceAtom, 'default')
  messageIdCounter = 0
  store.set(chatMessagesAtom, [INITIAL_MESSAGE])

  // If editing an existing label, load its data
  if (labelId) {
    store.set(loadLabelAtom, labelId)
  } else {
    // Check for Amazon listing pre-fill
    const prefill = store.get(prefillListingAtom)
    if (prefill) {
      // Map Amazon listing data → label fields
      const labelData: Partial<LabelData> = {
        productName: prefill.itemName,
        targetMarkets: ['US'] as Market[],
      }
      if (prefill.modelNumber) labelData.modelRef = prefill.modelNumber
      else if (prefill.sellerSku) labelData.modelRef = prefill.sellerSku
      if (prefill.manufacturer) labelData.manufacturerName = prefill.manufacturer
      if (prefill.countryOfOrigin) labelData.countryOfOrigin = prefill.countryOfOrigin
      if (prefill.upc) labelData.upcCode = prefill.upc
      if (prefill.material) labelData.material = prefill.material

      store.set(labelDataAtom, { ...defaultLabelData, ...labelData })
      // Mark source for breadcrumb navigation
      store.set(labelSourceAtom, 'amazon')
      // Show label type selection so user can choose Compliance or UPC
      store.set(onboardingStepAtom, 'labelType')
      store.set(labelTypeAtom, null)

      // Build a context summary of what was pre-filled
      const filledFields: string[] = ['product name', 'US market']
      if (labelData.modelRef) filledFields.push('model/SKU')
      if (labelData.manufacturerName) filledFields.push('manufacturer')
      if (labelData.countryOfOrigin) filledFields.push('country of origin')
      if (labelData.upcCode) filledFields.push('UPC code')
      if (labelData.material) filledFields.push('material')

      // Seed the chat with pre-fill info — label type selection shown first
      messageIdCounter = 1
      store.set(chatMessagesAtom, [
        INITIAL_MESSAGE,
        {
          id: nextId(),
          role: 'assistant',
          content: `I've loaded **${prefill.itemName}** from your Amazon listings (${filledFields.join(', ')}). What type of label would you like to create?`,
        },
      ])
      // Clear the pre-fill atom so it doesn't re-trigger
      store.set(prefillListingAtom, null)
    }
  }

  return null
}
